import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Camera, RefreshCw, AlertCircle, CheckCircle2, MapPin } from "lucide-react";
import axios from "axios";
import { baseUrl, environment } from "../../environment";
import { useSelector } from "react-redux";
import { StoreState } from "../../models/reduxModels";
import { compressAttendanceImage } from "../../utility/imageCompression";
import { Cookies } from "react-cookie";
import { useDispatch } from "react-redux";
import { ApiCallErrorAction } from "../../stores/actions/apiStatusActions";
import { LogOutAction } from "../../stores/actions/userAction";

type CaptureMode = "checkin" | "checkout";

interface IPhoneCameraModalProps {
  open: boolean;
  onClose: () => void;
  mode: CaptureMode;
  onSuccess?: () => void;
}

const IPhoneCameraModal: React.FC<IPhoneCameraModalProps> = ({
  open,
  onClose,
  mode,
  onSuccess,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dispatch = useDispatch()
  const streamRef = useRef<MediaStream | null>(null);

  const userDetails = useSelector((state: StoreState) => state.user.userDetails);

  const [cameraState, setCameraState] = useState<
    "initial" | "requesting" | "active" | "countdown" | "verifying" | "success" | "error"
  >("initial");
  const [errorType, setErrorType] = useState<"none" | "camera_denied" | "location_denied" | "general_error">("none");
  const [errorMsg, setErrorMsg] = useState("");
  const [countdown, setCountdown] = useState<number | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [location, setLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);
  const [ip, setIp] = useState("");

  const getIp = useCallback(async () => {
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      setIp(data.ip);
    } catch {
      setIp("");
    }
  }, []);

  const validateLocation = (lat: any, lon: any): boolean => {
    if (lat === null || lat === undefined || lon === null || lon === undefined) return false;
    const nLat = Number(lat);
    const nLon = Number(lon);
    if (isNaN(nLat) || isNaN(nLon)) return false;
    if (nLat === 0 && nLon === 0) return false;
    if (nLat < -90 || nLat > 90) return false;
    if (nLon < -180 || nLon > 180) return false;
    return true;
  };

  const getLocation = useCallback(() => {
    return new Promise<{ lat: number; lon: number } | null>((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      // Try High Accuracy GPS first with a 6-second timeout
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          if (validateLocation(lat, lon)) {
            resolve({ lat, lon });
          } else {
            tryLowAccuracy();
          }
        },
        (err) => {
          // If explicitly PERMISSION_DENIED (code 1), do not retry low accuracy
          if (err && err.code === 1) {
            resolve(null);
            return;
          }
          // On TIMEOUT (code 3) or POSITION_UNAVAILABLE (code 2), fallback to low accuracy (Wi-Fi/Cellular/Cache)
          tryLowAccuracy();
        },
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 }
      );

      function tryLowAccuracy() {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const lat = pos.coords.latitude;
            const lon = pos.coords.longitude;
            if (validateLocation(lat, lon)) {
              resolve({ lat, lon });
            } else {
              resolve(null);
            }
          },
          () => resolve(null),
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
        );
      }
    });
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    // Always stop any existing stream first to ensure clean state
    stopCamera();

    // 1. Online check
    if (!navigator.onLine) {
      setCameraState("error");
      setErrorType("general_error");
      setErrorMsg("Internet connection required. Attendance operations are unavailable while offline.");
      return;
    }

    // 2. Secure context check for browser media API rules
    const isSecureContext =
      window.isSecureContext ||
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (!isSecureContext && !navigator.mediaDevices?.getUserMedia) {
      setCameraState("error");
      setErrorType("general_error");
      setErrorMsg("Camera API requires an HTTPS / Secure connection or localhost. Please access the website over HTTPS.");
      return;
    }

    setCameraState("requesting");
    setErrorType("none");
    setErrorMsg("");
    setCapturedImage(null);
    setLocation(null);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setErrorType("camera_denied");
        throw new Error("Camera not supported or disabled by browser settings. Please allow camera access in Safari/Browser Settings.");
      }

      // Step 1: Prompt for Camera Permission IMMEDIATELY
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      } catch (camErr: any) {
        setErrorType("camera_denied");
        throw new Error("Camera access was denied. Please allow camera permission to capture selfie.");
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => { });
      }

      // Step 2: Prompt for Location/GPS Permission
      getIp();
      const loc = await getLocation();
      if (!loc || !validateLocation(loc.lat, loc.lon)) {
        setErrorType("location_denied");
        throw new Error("Location/GPS access was denied or timed out. Please enable Location Services in Phone Settings -> Privacy & Security -> Location Services -> Safari/Browser, and allow site location access.");
      }
      setLocation(loc);

      setErrorType("none");
      setCameraState("active");
    } catch (err: any) {
      setCameraState("error");
      setErrorMsg(err.message || "Failed to initialize camera or geolocation permissions.");
    }
  }, [getIp, getLocation, stopCamera]);

  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
      setCameraState("initial");
      setErrorType("none");
      setCapturedImage(null);
      setErrorMsg("");
      setCountdown(null);
    }
  }, [open, startCamera, stopCamera]);

  useEffect(() => {
    if (!open || errorType !== "location_denied" || cameraState !== "error") return;

    const handleRecheckOnFocus = async () => {
      if (document.visibilityState === "visible") {
        setIsCheckingLocation(true);
        const loc = await getLocation();
        setIsCheckingLocation(false);
        if (loc && validateLocation(loc.lat, loc.lon)) {
          setLocation(loc);
          setErrorType("none");
          setErrorMsg("");
          setCameraState("active");
        }
      }
    };

    window.addEventListener("focus", handleRecheckOnFocus);
    document.addEventListener("visibilitychange", handleRecheckOnFocus);

    return () => {
      window.removeEventListener("focus", handleRecheckOnFocus);
      document.removeEventListener("visibilitychange", handleRecheckOnFocus);
    };
  }, [open, errorType, cameraState, getLocation]);

  const requestCameraPermission = () => {
    startCamera();
  };

  const requestLocationPermission = async () => {
    setIsCheckingLocation(true);
    setErrorMsg("");
    setErrorType("none");

    if (!streamRef.current) {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: "user",
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: false,
          });
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play().catch(() => { });
          }
        }
      } catch (camErr: any) {
        setIsCheckingLocation(false);
        setErrorType("camera_denied");
        setErrorMsg("Camera access was denied. Please allow camera permission to capture selfie.");
        setCameraState("error");
        return;
      }
    }

    getIp();
    const loc = await getLocation();
    setIsCheckingLocation(false);

    if (loc && validateLocation(loc.lat, loc.lon)) {
      setLocation(loc);
      setErrorType("none");
      setErrorMsg("");
      setCameraState("active");
    } else {
      setErrorType("location_denied");
      setErrorMsg(
        "Location/GPS access is denied or blocked by browser settings. Please follow the iPhone Settings guide below to allow location access."
      );
      setCameraState("error");
    }
  };

  const handleInitiateCapture = () => {
    if (cameraState !== "active") return;
    setCapturedImage(null);
    setCameraState("countdown");
    setCountdown(3);
  };

  const submitAttendance = useCallback(
    async (imageDataUrl: string) => {
      if (!navigator.onLine) {
        setCameraState("error");
        setErrorMsg("Internet connection lost. Attendance cannot be submitted offline.");
        return;
      }

      if (!location || !validateLocation(location.lat, location.lon)) {
        setCameraState("error");
        setErrorMsg("Mandatory latitude and longitude coordinates missing. Cannot submit attendance.");
        return;
      }

      setCameraState("verifying");

      try {
        const res = await fetch(imageDataUrl);
        const blob = await res.blob();
        let file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

        if (file.size > 200 * 1024) {
          file = await compressAttendanceImage(file);
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("lat", String(location.lat));
        formData.append("long", String(location.lon));
        if (ip) formData.append("ip", ip);
        formData.append("attendance_mode", "online");
        formData.append("platform", "app");
        formData.append("os", "ios")

        const token = userDetails?.accessToken;

        const response = await axios.post(
          baseUrl + environment.urls.giveAttendanceApp,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.status === 200) {
          setCameraState("success");
          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 1500);
        } else {
          setCameraState("error");
          setErrorMsg(response.data?.message || "Face verification failed. Please try again.");
        }
      } catch (error: any) {
        const backendMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data === "string" ? error.response.data : undefined);

        if (error.response?.status === 401) {
          let cook = new Cookies();
          dispatch(
            ApiCallErrorAction({
              errorCode: error?.response?.data?.errorCode || "",
              message: backendMessage || "Please Login again to continue.",
            })
          );
          cook.remove("user", { path: "/" });
          dispatch(LogOutAction());
        } else if (error.response?.status === 500) {
          dispatch(
            ApiCallErrorAction({
              errorCode: error?.response?.data?.errorCode || "",
              message: backendMessage || "Internal server error. Please try again.",
            })
          );
        }
        setCameraState("error");
        setErrorMsg(
          backendMessage ||
          (error.message && !error.message.startsWith("Request failed with status code") ? error.message : null) ||
          "Failed to submit attendance. Please try again."
        );
      }
    },
    [dispatch, ip, location, onClose, onSuccess, userDetails?.accessToken]
  );

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      console.log("[IPhoneCameraModal] Video or Canvas ref missing during photo capture.");
      setCameraState("error");
      setErrorMsg("Camera video feed unavailable. Please tap Retry Attendance.");
      return;
    }
    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setCapturedImage(dataUrl);
    stopCamera();
    submitAttendance(dataUrl);
  }, [stopCamera, submitAttendance]);

  const capturePhotoRef = useRef(capturePhoto);
  useEffect(() => {
    capturePhotoRef.current = capturePhoto;
  }, [capturePhoto]);

  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      setCountdown(null);
      capturePhotoRef.current();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-3 sm:p-4 animate-fadeIn">
      <div className="relative w-full max-w-[360px] sm:max-w-sm max-h-[95vh] bg-gray-900 rounded-3xl overflow-hidden text-white border border-gray-800 shadow-2xl flex flex-col items-center">
        {/* Header Bar */}
        <div className="w-full flex items-center justify-between px-4 py-3.5 bg-gray-950/60 border-b border-gray-800/60 shrink-0">
          <span className="text-xs sm:text-sm font-semibold text-gray-200 uppercase tracking-wide">
            {mode === "checkin" ? "Clock In Attendance" : "Clock Out Attendance"}
          </span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Camera View Container */}
        <div className="relative w-full aspect-[3/4] max-h-[55vh] bg-black flex items-center justify-center overflow-hidden">
          <canvas ref={canvasRef} className="hidden" />

          {capturedImage ? (
            <img
              src={capturedImage}
              alt="Selfie"
              className="w-full h-full object-cover transform -scale-x-100"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover transform -scale-x-100"
            />
          )}

          {/* Circular Face Oval Overlay */}
          {(cameraState === "active" || cameraState === "countdown" || cameraState === "requesting") && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-64 sm:w-56 sm:h-76 rounded-full border-4 border-[#F97316] shadow-[0_0_40px_rgba(249,115,22,0.4)] flex flex-col items-center justify-center">
                {countdown !== null && (
                  <span className="text-5xl sm:text-6xl font-extrabold text-white drop-shadow-md animate-pulse">
                    {countdown}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Loading / Verifying Overlay */}
          {(cameraState === "requesting" || cameraState === "verifying") && (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-xs flex flex-col items-center justify-center p-5 text-center">
              <RefreshCw className="w-9 h-9 text-[#F97316] animate-spin mb-3" />
              <p className="text-xs sm:text-sm font-semibold">
                {cameraState === "requesting"
                  ? "Accessing camera & GPS location..."
                  : "Verifying face with backend..."}
              </p>
            </div>
          )}

          {/* Success Overlay */}
          {cameraState === "success" && (
            <div className="absolute inset-0 bg-emerald-950/80 backdrop-blur-xs flex flex-col items-center justify-center p-5 text-center animate-fadeIn">
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mb-3 animate-bounce" />
              <p className="text-base sm:text-lg font-bold text-emerald-300">Attendance Marked Successfully!</p>
            </div>
          )}

          {/* Error Overlay */}
          {cameraState === "error" && (
            <div className="absolute inset-0 bg-gray-950/95 flex flex-col items-center justify-center p-5 text-center">
              <AlertCircle className="w-10 h-10 text-rose-500 mb-3" />
              <p className="text-xs sm:text-sm font-semibold text-rose-300 mb-4 leading-relaxed max-w-[280px]">
                {errorMsg}
              </p>

              {errorType === "camera_denied" && (
                <button
                  onClick={requestCameraPermission}
                  className="px-4 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-xl text-xs flex items-center gap-2 shadow-md active:scale-95 transition-all"
                >
                  <Camera className="w-4 h-4" /> Enable Camera Access
                </button>
              )}

              {errorType === "location_denied" && (
                <div className="flex flex-col items-center w-full max-w-xs space-y-3">
                  <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-3 text-left w-full space-y-2 text-[11px] text-gray-300">
                    <p className="font-semibold text-rose-400 text-xs flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 shrink-0 text-rose-400" /> Location Access Blocked on iPhone
                    </p>
                    <div className="space-y-1.5 leading-tight text-gray-300">
                      <p><span className="font-bold text-white">Step 1:</span> Open iPhone <span className="text-amber-300 font-semibold">Settings</span> &gt; <span className="text-amber-300 font-semibold">Privacy &amp; Security</span> &gt; <span className="text-amber-300 font-semibold">Location Services</span> (Turn ON).</p>
                      <p><span className="font-bold text-white">Step 2:</span> Tap <span className="text-amber-300 font-semibold">Safari</span> (or browser) &gt; Choose <span className="text-emerald-400 font-semibold">"While Using the App"</span> &amp; enable <span className="text-emerald-400 font-semibold">"Precise Location"</span>.</p>
                      <p><span className="font-bold text-white">Step 3:</span> In Safari address bar, tap <span className="text-sky-300 font-semibold">'aA' / Website Settings</span> &gt; <span className="text-sky-300 font-semibold">Location</span> &gt; <span className="text-emerald-400 font-semibold">Allow</span>.</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 w-full">
                    <button
                      onClick={requestLocationPermission}
                      disabled={isCheckingLocation}
                      className="flex-1 py-2.5 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white font-semibold rounded-xl text-xs flex items-center justify-center gap-2 shadow-md active:scale-95 transition-all"
                    >
                      {isCheckingLocation ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" /> Checking GPS Access...
                        </>
                      ) : (
                        <>
                          <MapPin className="w-4 h-4" /> Check Location Access Again
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => window.location.reload()}
                      className="px-3 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-200 font-medium rounded-xl text-xs flex items-center justify-center gap-1 transition-all"
                      title="Reload Page"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Reload
                    </button>
                  </div>
                </div>
              )}

              {(errorType === "general_error" || errorType === "none") && (
                <button
                  onClick={startCamera}
                  className="px-4 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-xl text-xs flex items-center gap-2 shadow-md active:scale-95 transition-all"
                >
                  <RefreshCw className="w-4 h-4" /> Retry Attendance
                </button>
              )}
            </div>
          )}
        </div>

        {/* Location Status Bar */}
        <div className="w-full px-4 py-2 bg-gray-950/80 border-t border-gray-800 flex items-center justify-between text-[10px] sm:text-[11px] text-gray-400 shrink-0">
          <div className="flex items-center gap-1 min-w-0">
            <MapPin className="w-3.5 h-3.5 text-[#F97316] shrink-0" />
            <span className="truncate">
              {location
                ? `GPS: ${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}`
                : "Acquiring coordinates..."}
            </span>
          </div>
          <span className="text-emerald-400 font-medium shrink-0 ml-2">Online Mode</span>
        </div>

        {/* Bottom Control Actions */}
        <div className="w-full p-3.5 bg-gray-950 flex items-center justify-center shrink-0">
          {cameraState === "active" && (
            <button
              onClick={handleInitiateCapture}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#F97316] hover:bg-[#EA580C] flex items-center justify-center text-white shadow-lg active:scale-95 transition-transform"
            >
              <Camera className="w-7 h-7 sm:w-8 sm:h-8" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IPhoneCameraModal;
